import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  GraduationCap, 
  BookOpen, 
  Calculator, 
  Hash,
  ChevronRight,
  Sparkles,
  Baby,
  User,
  BarChart3
} from 'lucide-react';
import TutorToby from '@/components/TutorToby';
import MissSpark from '@/components/MissSpark';

const LandingPage = () => {
  const navigate = useNavigate();

  const modules = [
    {
      id: 'abcd',
      title: 'Learn ABCD',
      description: 'Sing along with Miss Spark!',
      tutor: 'spark',
      path: '/learn/abcd',
      age: '1-3 years',
      icon: <BookOpen className="w-8 h-8" />,
      gradient: 'from-spark to-pink-400',
    },
    {
      id: 'counting',
      title: 'Counting 1-10',
      description: 'Show your fingers!',
      tutor: 'spark',
      path: '/learn/counting',
      age: '1-3 years',
      icon: <Hash className="w-8 h-8" />,
      gradient: 'from-teal to-aqua',
    },
    {
      id: 'tables',
      title: 'Tables Challenge',
      description: 'Practice with Tutor Toby!',
      tutor: 'toby',
      path: '/learn/tables',
      age: '5th-6th Grade',
      icon: <Calculator className="w-8 h-8" />,
      gradient: 'from-seagreen to-teal',
    },
  ];

  return (
    <div className="min-h-screen relative z-10">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-seagreen to-teal rounded-2xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="font-heading text-2xl text-foreground">TutorBuddy</span>
              <span className="text-seagreen">.io</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              data-testid="progress-btn"
              variant="ghost"
              onClick={() => navigate('/progress')}
              className="text-muted-foreground hover:text-seagreen"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Progress
            </Button>
            <Button 
              data-testid="start-btn"
              onClick={() => navigate('/learn/abcd')}
              className="btn-playful btn-toby"
            >
              Start Learning
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-seagreen/10 text-seagreen rounded-full text-sm font-medium mb-6">
              Fun Learning for Kids
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-foreground leading-tight mb-6">
              Where Learning Feels<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-seagreen to-teal">Like Play!</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Meet your AI tutors who make learning ABCD, counting, and multiplication tables super fun!
            </p>
          </motion.div>

          {/* Tutor Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Tutor Toby Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="character-card toby cursor-pointer"
              onClick={() => navigate('/learn/tables')}
              data-testid="toby-card"
            >
              <div className="flex flex-col h-full justify-between text-white">
                <div>
                  <span className="age-badge junior mb-4 inline-block bg-white/20">
                    <User className="w-4 h-4" />
                    5th-6th Grade
                  </span>
                  <h2 className="text-3xl font-heading mb-2">Tutor Toby</h2>
                  <p className="text-white/80 mb-4">
                    Witty, friendly, speaks Hinglish! Perfect for mastering multiplication tables.
                  </p>
                  <p className="text-sm text-white/60 italic">
                    "Arre beta, 13 times 7 kya hota hai?"
                  </p>
                </div>
                <div className="mt-6">
                  <TutorToby mood="happy" size="small" />
                </div>
              </div>
            </motion.div>

            {/* Miss Spark Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="character-card spark cursor-pointer"
              onClick={() => navigate('/learn/abcd')}
              data-testid="spark-card"
            >
              <div className="flex flex-col h-full justify-between text-white">
                <div>
                  <span className="age-badge toddler mb-4 inline-block bg-white/20">
                    <Baby className="w-4 h-4" />
                    Ages 1-3
                  </span>
                  <h2 className="text-3xl font-heading mb-2">Miss Spark</h2>
                  <p className="text-white/80 mb-4">
                    Musical, energetic, super encouraging! Perfect for ABCD and counting.
                  </p>
                  <p className="text-sm text-white/60 italic">
                    "A for Apple, yummy yummy Apple! 🎵"
                  </p>
                </div>
                <div className="mt-6">
                  <MissSpark mood="happy" size="small" />
                </div>
              </div>
            </motion.div>
          </div>

          {/* Module Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-heading text-center mb-8">Choose Your Adventure</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {modules.map((module, index) => (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(module.path)}
                  className={`module-card bg-gradient-to-br ${module.gradient} cursor-pointer`}
                  data-testid={`module-${module.id}`}
                >
                  <div className="module-card-content">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                      {module.icon}
                    </div>
                    <h3 className="text-xl font-heading mb-1">{module.title}</h3>
                    <p className="text-sm text-white/80 mb-2">{module.description}</p>
                    <span className="text-xs text-white/60">{module.age}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-seagreen to-teal rounded-3xl p-8 md:p-12 text-center text-white"
          >
            <h2 className="text-3xl font-heading mb-4">Ready to Start Learning?</h2>
            <p className="text-white/80 mb-6">Jump right in and have fun while you learn!</p>
            <Button
              data-testid="cta-start-btn"
              onClick={() => navigate('/learn/abcd')}
              size="lg"
              className="bg-white text-seagreen hover:bg-white/90 text-lg px-10 font-heading font-semibold rounded-2xl"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Let's Go!
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-white/50 border-t border-border">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-seagreen to-teal rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-lg text-foreground">TutorBuddy.io</span>
          </div>
          <p className="text-muted-foreground text-sm">Making learning fun for every child!</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
